/** Меню отчётов «кто о ком пишет» у чипа карточки игры в расстановке (вынесено из OoAssignScreen 13.10).
 *  Оператор состава — долгий тап по чипу (как с 19.08); игрок учебной игры — тап (13.10, владелец: «при
 *  расстановке чтобы у ОО была возможность запросить отчёты как у операторов, так и у играющих операторов,
 *  отчёты друг о друге»). «Отчёт ОБ …» — выбрать, кто пишет; «Отчёт ОТ …» — о ком. Кандидаты: состав игры →
 *  игроки → на филиале. Быстрые варианты учебной игры: у оператора «От игроков» / «Об игроках», у игрока
 *  «От операторов» / «Об операторах игры». Заявки — черновик доски: на сервер по «Сохранить»/«Отправить» (usePeerRequests, 13.10). */
import { useEffect, useState } from "react";
import type { PeerReqItem } from "../../hooks/usePeerRequests";
import type { LearningPlayer } from "../../hooks/useLearningBoard";
import { useClampPop } from "./useClampPop";
import css from "./board.module.css";

/** Общее для всех меню отчётов одной карточки игры. */
export interface PeerCtx {
  crew: string[];
  /** 13.10: играющие сотрудники учебной игры — не назначения, у них нет чипа состава */
  players: LearningPlayer[];
  /** все, кто сегодня на играх этого филиала («На филиале») */
  branchIds: Set<string>;
  /** имя по id — и операторы, и игроки: пара видна сразу с именами, без сырых id (21.08 / 13.10) */
  nameOf: (id: string) => string;
  onPeerAdd: (requests: PeerReqItem[]) => void;
}

const item = (ctx: PeerCtx, a: string, s: string): PeerReqItem => ({ author_id: a, subject_id: s, author_name: ctx.nameOf(a), subject_name: ctx.nameOf(s) });

/** Пары [автор, о ком] → заявки; «сам о себе» в быстрых вариантах учебной игры не запрашиваем (13.10). */
export function peerPairs(ctx: PeerCtx, pairs: [string, string][]): PeerReqItem[] {
  return pairs.filter(([a, s]) => a !== s).map(([a, s]) => item(ctx, a, s));
}

/** «Друг о друге» (13.10): каждый оператор игры — о каждом игроке, каждый игрок — о каждом операторе. */
export function eachOtherPairs(ctx: PeerCtx): PeerReqItem[] {
  return peerPairs(ctx, ctx.crew.flatMap((c) => ctx.players.flatMap((p): [string, string][] => [[c, p.id], [p.id, c]])));
}

export function PeerMenu({ personId, kind, reasons = [], toggle = false, onClose, ...ctx }: PeerCtx & {
  personId: string;
  /** crew — оператор состава; player — играющий сотрудник учебной игры */
  kind: "crew" | "player";
  /** «Почему красный» у оператора */
  reasons?: string[];
  /** тап по своему чипу меню не закрывает «мимо» — чип сам переключает меню (игроки, 13.10) */
  toggle?: boolean;
  onClose: () => void;
}) {
  /** Пикер: «about» → выбрать, кто пишет; «from» → выбрать, о ком. */
  const [pick, setPick] = useState<"about" | "from" | null>(null);
  const ref = useClampPop<HTMLDivElement>(`${personId}|${pick ?? ""}`); /* кламп вьюпортом; пикер меняет высоту — пересчёт (26.08) */
  useEffect(() => { // клик в любое другое место экрана — закрыть меню (20.08, Саша)
    const onDown = (e: PointerEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t?.closest?.(`.${css.chipMenu}`) && !(toggle && t && ref.current?.parentElement?.contains(t))) onClose();
    };
    document.addEventListener("pointerdown", onDown, true); return () => document.removeEventListener("pointerdown", onDown, true);
  }, [onClose, toggle, ref]);

  const { crew, players, nameOf } = ctx;
  const name = nameOf(personId);
  const add = (reqs: PeerReqItem[]) => { if (reqs.length) ctx.onPeerAdd(reqs); onClose(); };

  if (!pick) {
    return (
      <div className={css.chipMenu} ref={ref}>
        {reasons.length ? <div className={css.chipReason}>Почему красный: {reasons.join(" · ")}</div> : null}
        <button type="button" className={css.chipMenuItem} onClick={() => setPick("about")}>Отчёт ОБ {name}…</button>
        <button type="button" className={css.chipMenuItem} onClick={() => setPick("from")}>Отчёт ОТ {name}…</button>
        <button type="button" className={`${css.chipMenuItem} ${css.chipMenuClose}`} onClick={onClose}>Закрыть</button>
      </div>
    );
  }

  const isAbout = pick === "about";
  /* кандидаты — без самого человека; каждая группа по алфавиту имён, человек — только в одной группе */
  const byName = (a: string, b: string) => nameOf(a).localeCompare(nameOf(b), "ru");
  const sameGame = crew.filter((x) => x !== personId).sort(byName);
  const playing = players.map((p) => p.id).filter((x) => x !== personId && !sameGame.includes(x)).sort(byName);
  const branch = [...ctx.branchIds].filter((x) => x !== personId && !sameGame.includes(x) && !playing.includes(x)).sort(byName);
  const pickOne = (c: string) => add(isAbout ? [item(ctx, c, personId)] : [item(ctx, personId, c)]);
  /* быстрый вариант учебной игры: оператору — все игроки, игроку — весь состав */
  const others = kind === "crew" ? players.map((p) => p.id) : crew;
  const quick = peerPairs(ctx, others.map((o): [string, string] => (isAbout ? [o, personId] : [personId, o])));
  const quickLbl = kind === "crew" ? (isAbout ? "От игроков" : "Об игроках") : (isAbout ? "От операторов" : "Об операторах игры");
  const group = (title: string, ids: string[]) => (ids.length ? (
    <>
      <div className={css.chipMenuSub}>{title}</div>
      {ids.map((s) => (
        <button key={s} type="button" className={css.chipMenuItem} onClick={() => pickOne(s)}>
          {nameOf(s)}
        </button>
      ))}
    </>
  ) : null);

  return (
    <div className={css.chipMenu} ref={ref}>
      <div className={css.chipMenuLbl}>{isAbout ? `Отчёт ОБ ${name} — кто пишет?` : `Отчёт ОТ ${name} — о ком?`}</div>
      {/* «Весь состав игры» — как с 19.08: пишет остальной состав; сольный состав — отчёт о себе */}
      {isAbout && kind === "crew" ? (
        <button type="button" className={css.chipMenuItem} onClick={() => { const rest = crew.filter((x) => x !== personId); add(rest.length ? rest.map((a) => item(ctx, a, personId)) : [item(ctx, personId, personId)]); }}>Весь состав игры</button>
      ) : null}
      {quick.length ? <button type="button" className={css.chipMenuItem} onClick={() => add(quick)}>{quickLbl}</button> : null}
      {sameGame.length === 0 && playing.length === 0 && branch.length === 0 ? <div className={css.chipReason}>Нет кандидатов</div> : null}
      {group("На этой игре", sameGame)}
      {group("Игроки", playing)}
      {group("На филиале", branch)}
      <button type="button" className={`${css.chipMenuItem} ${css.chipMenuClose}`} onClick={() => setPick(null)}>← Назад</button>
    </div>
  );
}
